/**
 * useAdaptiveNavTheme — translucent-glass nav scroll-spy theme flip.
 *
 * The top bar and the docked report sub-nav use translucent `--fm-glass-bar`:
 * the section behind them shows through the tinted glass. To stay legible the
 * nav must flip its own `data-theme` to match what is *currently underneath it*:
 *   - over a dark band (hero / map / `.fm-section-band--dark`) → `data-theme="dark"`
 *     which resolves the DS tokens to light-on-dark (light text + icons).
 *   - over light content → `data-theme="light"` → dark text on the same
 *     translucent glass surface.
 *
 * Implementation: a single IntersectionObserver watches every dark band. The
 * observer root margin collapses the viewport to a short detection band that
 * starts at the nav's bottom edge and reaches a few px further down, so a band
 * only counts while it is the thing physically behind the nav. The band has a
 * small depth (not a 1px hairline) on purpose: when the nav floats above a
 * *nested* scroll container (e.g. the planner report body, `overflow:auto`),
 * the dark band's visible rect is clipped by that container's top edge — which
 * coincides with the nav's bottom edge — so a 1px line exactly at that boundary
 * shares only its edge with the band and misses it. A short band reliably
 * overlaps the clipped band's first visible pixels. No polling, no scroll
 * handler.
 *
 * Motion: the theme value itself flips instantly (CSS owns the colour
 * transition). When the user prefers reduced motion the consuming CSS drops the
 * transition; the hook stays the same. Updates are coalesced through
 * requestAnimationFrame so a burst of intersection callbacks during a fast
 * scroll resolves to one state write, never a flashing cascade.
 */
import { useEffect, useState } from 'react';
import { useRouteTheme } from './routeThemeContext';

export type AdaptiveNavTheme = 'dark' | 'light';

/** Selector for surfaces that should force light-on-dark nav chrome. */
// Covers both dark band tones — `--dark` (immersive/map) and `--deep` (dark
// data surface) — plus the explicit `[data-fm-nav-theme="dark"]` opt-in marker.
const DARK_BAND_SELECTOR =
  '.fm-section-band--dark, .fm-section-band--deep, [data-fm-nav-theme="dark"]';

/**
 * Depth (px) of the detection band below the nav's bottom edge. Must be large
 * enough that a dark band clipped by a nested scroll container's top edge (which
 * sits at the nav's bottom edge) still overlaps the detection band by its first
 * visible pixels, but small enough that it samples only the surface immediately
 * behind the nav rather than content further down the page.
 */
const PROBE_DEPTH_PX = 8;

export interface UseAdaptiveNavThemeOptions {
  /**
   * Viewport-y, in px, at which to sample the band sitting *behind the nav*. Use
   * the nav strip's own bottom edge: the top bar samples at the top-bar height;
   * a docked report sub-nav samples at top-bar height + its own height. Pass 0
   * to fall back to the `--fm-topbar-h` token.
   */
  probeOffsetPx: number;
  /** Theme when no dark band is under the nav and the route theme is not dark. */
  fallback?: AdaptiveNavTheme;
  /** Disable the observer (e.g. SSR / no IntersectionObserver). */
  enabled?: boolean;
  /**
   * Optional DOM scope for dark-band discovery. App chrome should pass the
   * shell outlet so route mutations do not trigger body-wide rescans.
   */
  scopeSelector?: string;
  /** Shell-owned element; avoids cross-instance discovery in component galleries. */
  scopeElement?: Element | null;
}

const resolveProbeOffset = (probeOffsetPx: number): number => {
  if (Number.isFinite(probeOffsetPx) && probeOffsetPx > 0) return probeOffsetPx;
  // Read the DS token so callers can pass 0 before layout settles.
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--fm-topbar-h').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 52;
};

export function useAdaptiveNavTheme({
  probeOffsetPx,
  fallback = 'light',
  enabled = true,
  scopeSelector,
  scopeElement,
}: UseAdaptiveNavThemeOptions): AdaptiveNavTheme {
  // With no dark band behind it the bar takes the route's own theme: the glass
  // fill resolves in the route theme, so the fallback ink over a dark route
  // would paint dark text on dark glass.
  const routeTheme = useRouteTheme();
  const restingTheme: AdaptiveNavTheme = routeTheme === 'dark' ? 'dark' : fallback;
  const [theme, setTheme] = useState<AdaptiveNavTheme>(restingTheme);
  const [geometryVersion, setGeometryVersion] = useState(0);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    let rafId = 0;
    const scheduleGeometryRefresh = (): void => {
      if (rafId !== 0) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        setGeometryVersion((current) => current + 1);
      });
    };

    window.addEventListener('resize', scheduleGeometryRefresh);
    return () => {
      if (rafId !== 0) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleGeometryRefresh);
    };
  }, [enabled]);

  useEffect(() => {
    if (
      !enabled ||
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window) ||
      !('MutationObserver' in window)
    ) {
      setTheme(restingTheme);
      return undefined;
    }

    const scopeRoot =
      scopeElement ??
      (scopeSelector ? (document.querySelector(scopeSelector) ?? document.body) : document.body);
    const probeOffset = resolveProbeOffset(probeOffsetPx);
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    // Collapse the root box to a short detection band that starts at the nav's
    // bottom edge and reaches PROBE_DEPTH_PX further down: keep `probeOffset` of
    // the top, trim everything below `probeOffset + PROBE_DEPTH_PX`. A dark band
    // counts while it sits behind the nav at that line. The depth (not a 1px
    // hairline) lets the detection survive a nested scroll container whose top
    // edge clips the dark band exactly at the nav's bottom edge — see the file
    // header for the full rationale.
    const detectionDepth = Math.min(PROBE_DEPTH_PX, Math.max(1, viewportH - probeOffset));
    const bottomTrim = Math.max(0, viewportH - probeOffset - detectionDepth);
    const rootMargin = `-${probeOffset}px 0px -${bottomTrim}px 0px`;

    const intersecting = new Set<Element>();
    let themeRafId = 0;
    let syncRafId = 0;

    const flush = (): void => {
      themeRafId = 0;
      setTheme(intersecting.size > 0 ? 'dark' : restingTheme);
    };
    const scheduleThemeFlush = (): void => {
      if (themeRafId !== 0) return;
      themeRafId = window.requestAnimationFrame(flush);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersecting.add(entry.target);
          else intersecting.delete(entry.target);
        }
        scheduleThemeFlush();
      },
      { rootMargin, threshold: 0 },
    );

    let observed: Element[] = [];
    const sync = (): void => {
      const next = Array.from(scopeRoot.querySelectorAll(DARK_BAND_SELECTOR));
      // Stop watching bands that left the DOM (route/section swaps).
      for (const el of observed) {
        if (!next.includes(el)) {
          observer.unobserve(el);
          intersecting.delete(el);
        }
      }
      for (const el of next) {
        if (!observed.includes(el)) observer.observe(el);
      }
      observed = next;
      scheduleThemeFlush();
    };

    const scheduleSync = (): void => {
      if (syncRafId !== 0) return;
      syncRafId = window.requestAnimationFrame(() => {
        syncRafId = 0;
        sync();
      });
    };

    sync();
    // Re-sync when bands mount/unmount (route changes, async data, etc.).
    const mutationObserver = new MutationObserver(scheduleSync);
    mutationObserver.observe(scopeRoot, { childList: true, subtree: true });

    return () => {
      if (themeRafId !== 0) window.cancelAnimationFrame(themeRafId);
      if (syncRafId !== 0) window.cancelAnimationFrame(syncRafId);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [probeOffsetPx, restingTheme, enabled, scopeSelector, scopeElement, geometryVersion]);

  return theme;
}

export default useAdaptiveNavTheme;
